export type Portal = 'client' | 'platform';

export interface AuthUser {
  id: number;
  email: string;
  displayName: string;
  role: string;
  roleId?: number | null;
  /**
   * What this person may do, as sent back at sign-in.
   *
   * Presentation only. Hiding a button the API would refuse is a courtesy, not a
   * control — every one of these is checked again on the route, and the screens
   * are written on the assumption that this list can be stale or absent.
   */
  permissions?: string[];
  tenantId: number | null;
  tenantName: string | null;
  portal: Portal;
}

/** The permission keys the portal actually branches on. */
/**
 * The permission catalogue, mirrored for the portal.
 *
 * It was a partial mirror — thirteen of the server's eighteen — which is fine while
 * nothing needs the missing five and becomes a silent bug the moment something
 * does: `PERMISSION.contactsView` is `undefined`, `can(user, undefined)` is false,
 * and a destination disappears for everybody with no error anywhere.
 *
 * `tools/check-theme-tokens.mjs` is not the place for this; `PermissionCatalogueTests`
 * already checks the server's catalogue against migration 0023 and the route table,
 * and `tools/check-permission-mirror.mjs` now checks this file against the same
 * catalogue. Three places name these strings and none of them can drift silently.
 */
export const PERMISSION = {
  inboxView: 'inbox.view',
  inboxSend: 'inbox.send',
  inboxAssign: 'inbox.assign',
  inboxViewAll: 'inbox.view_all',
  contactsView: 'contacts.view',
  contactsWrite: 'contacts.write',
  contactsImport: 'contacts.import',
  contactsExport: 'contacts.export',
  segmentsWrite: 'segments.write',
  campaignsView: 'campaigns.view',
  campaignsWrite: 'campaigns.write',
  campaignsLaunch: 'campaigns.launch',
  templatesWrite: 'templates.write',
  tagsWrite: 'tags.write',
  cannedWrite: 'canned.write',
  catalogueManage: 'catalogue.manage',
  automationWrite: 'automation.write',
  teamManage: 'team.manage',
  settingsWrite: 'settings.write',
  reportsView: 'reports.view',
  shopManage: 'shop.manage',
  shopPrices: 'shop.prices',
} as const;

export function can(user: AuthUser | null | undefined, permission: string): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

export interface Conversation {
  id: number;
  status: number;                 // 0 open, 1 pending, 2 resolved
  unreadCount: number;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  lastMessageDirection: number | null;
  /** The message the two fields above describe. The live patch matches on it, so a
      receipt for an older message cannot move a row that has since moved on. */
  lastMessageId?: number | null;
  /** 0 queued · 1 sent · 2 delivered · 3 read · 4 failed. Null on an inbound row —
      a tick is for a message we sent — and the list draws nothing when it is. */
  lastMessageStatus?: number | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  windowExpiresAt: string | null;
  /** The response-target deadline currently running (first reply until somebody
      replies, then resolution). Null when the account measures nothing or the
      conversation is closed. */
  slaDueAt?: string | null;
  assignedAgentId: number | null;
  assignedAgentName: string | null;
  waId: string;
  displayName: string | null;
  channelId: number;
  channelNumber: string | null;
  /** The number's verified name. The line under each row when a tenant runs more
      than one, and the reason every row otherwise looks like every other row. */
  channelName: string | null;
  tags: AppliedTag[];

  contactId: number;
  /** The customer's decision. Null when they have not made one. */
  optedOutAt: string | null;
  /** Ours. Deliberately a separate field from optedOutAt — see 0029. */
  blockedAt: string | null;
}

export interface Agent {
  id: number;
  displayName: string;
  email: string;
  role: string;
}

/** Keyset page. `nextCursor` null means the end — the client stops asking. */
/**
 * One of the tenant's own numbers.
 *
 * Quality and tier are here rather than only in the admin portal because they are
 * what decides whether this tenant's messages arrive, and the people sending them
 * were the last to know. Both come from Meta and are null until a sync has run —
 * a null tier is read by the dispatcher as the smallest one, which is why the home
 * screen says "not yet reported" rather than drawing a blank.
 */
export interface TenantChannel {
  id: number;
  displayPhoneNumber: string;
  /** Meta's display name — a copy, refreshed on every recheck; changed in WhatsApp Manager. */
  verifiedName: string | null;
  /** The team's own name for the number ("Marketing"). Ours: Meta never sees it. */
  label: string | null;
  /** GREEN, YELLOW or RED, as Meta words it. Null until first synced. */
  qualityRating: string | null;
  /** TIER_250, TIER_2K, TIER_10K, TIER_100K, TIER_UNLIMITED. Null until first synced. */
  messagingLimitTier: string | null;
  isActive: boolean;
  isCoexistence?: boolean;
  /** Set when the handset was unlinked (account_update). Only on `channels({ all: true })`. */
  disconnectedAt?: string | null;
  /** Meta's own word for why, when it gave one: CHANGE_NUMBER, COMPANION_INACTIVITY, BUSINESS_DOWNGRADE… */
  disconnectReason?: string | null;
  /** USER or SYSTEM — the client did it, or Meta did it to them. */
  disconnectInitiatedBy?: string | null;
}

/** What a disconnect reason means, in the client's words. */
export function disconnectExplanation(c: Pick<TenantChannel, 'disconnectReason' | 'disconnectInitiatedBy' | 'isCoexistence'>): string {
  switch (c.disconnectReason) {
    case 'CHANGE_NUMBER': return 'the number is being changed on the phone';
    case 'COMPANION_INACTIVITY': return 'the WhatsApp Business app on the phone had been inactive for too long';
    case 'BUSINESS_DOWNGRADE': return 'a billing problem on the Meta side';
    case 'USER_RE_REGISTERED': return 'the number was registered somewhere else';
    case 'ACCOUNT_DISCONNECTED': return 'it was disconnected from the WhatsApp Business app on the phone';
    case 'ACCOUNT_OFFBOARDED': return 'the number was registered afresh on the phone (a new phone, a reinstall or a new verification), which ends the link';
  }
  if (c.disconnectInitiatedBy === 'USER') return 'it was unlinked from the phone';
  if (c.disconnectInitiatedBy === 'SYSTEM') return 'WhatsApp unlinked it';
  return c.isCoexistence ? 'it was unlinked from the WhatsApp Business app on the phone, or by WhatsApp' : 'WhatsApp disconnected it';
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/** One chat's answer to a forward. `code` is stable; `problem` is for the agent. */
export interface ForwardResult {
  conversationId: number;
  ok: boolean;
  messageId: number | null;
  code: string | null;
  problem: string | null;
}

export interface Message {
  id: number;
  direction: number;
  source: number;
  messageType: string;
  body: string | null;
  status: number;
  errorCode: string | null;
  errorTitle: string | null;
  waMessageId: string | null;
  createdAt: string;
  sentByAgentId: number | null;
  sentByAgentName: string | null;
  /** For an outbound message no agent typed: "via API · CRM", "Bot", "Broadcast". Null otherwise. */
  sentVia: string | null;

  /** For an inbound `order`: the basket, JSON — see `parseOrder`. Null otherwise. */
  orderPayload?: string | null;

  /** For a sent `template`: header, footer and buttons, JSON — see `parseTemplateCard`. Null otherwise. */
  templatePayload?: string | null;

  /** Attachment, when the message carries one. */
  mediaAssetId: number | null;
  /** 0 pending, 1 stored, 2 failed — the bytes may not have arrived yet. */
  /** 0 pending, 1 stored, 2 failed (expired before fetch), 3 blocked (refused on purpose). */
  mediaStatus: number | null;
  /** Set only when mediaStatus is 3: why the file was refused, in words for the agent. */
  mediaBlockedReason?: string | null;
  mediaMimeType: string | null;
  mediaFileName: string | null;
  mediaByteSize: number | null;
  /** Signed, short-lived, ready to use directly in src. Null until stored. */
  mediaUrl: string | null;

  /**
   * The message this one quotes — the phone's "reply to". `id` is ours, for the
   * scroll-to; `unavailable` when the original is not in this thread any more
   * (deleted on the phone, aged out, or older than the history we hold), in
   * which case the phone shows the same sentence we do.
   */
  replyTo?: {
    id: number | null;
    direction: number | null;
    type: string | null;
    body: string | null;
    agentName: string | null;
    fileName: string | null;
    unavailable: boolean;
  } | null;

  /**
   * Deleted for everyone from the WhatsApp Business app, on a coexistence number.
   *
   * The body is already null when this is set — the text is not kept, because keeping
   * it would make the retraction visible to nobody and the retracted words visible to
   * everybody. So the bubble has to draw itself from this alone; without it an agent
   * sees an empty bubble and reads it as a bug.
   */
  revokedAt?: string | null;

  /**
   * Corrected from the WhatsApp Business app. `body` is the corrected text, so this is
   * the only thing that says the customer first saw something else.
   */
  editedAt?: string | null;

  /** Everyone who has reacted to this message. Empty, never absent. */
  reactions: AppliedReaction[];
}

export interface AppliedReaction {
  emoji: string;
  /** 0 the contact, 1 one of our agents. */
  reactorKind: number;
  agentId: number | null;
  agentName: string | null;
  reactedAt: string;
}

/**
 * The six WhatsApp offers on a long-press, in its order.
 *
 * Deliberately not configurable. The value of a reaction bar is that the emoji
 * are in the same place every time, and a per-tenant list is a preference nobody
 * asked for that costs a settings screen and a migration.
 */
export const REACTION_CHOICES = ['\u{1F44D}', '\u2764\uFE0F', '\u{1F602}', '\u{1F62E}', '\u{1F622}', '\u{1F64F}'] as const;

/**
 * Collapses a message's reactions into one chip per distinct emoji.
 *
 * `mine` drives the highlighted state, and it is keyed on the agent id rather
 * than on "an agent reacted": in a shared inbox somebody else's thumbs-up must
 * not look like your own, or you will tap it to remove a reaction that is not
 * yours and nothing will happen.
 */
export function groupReactions(
  reactions: AppliedReaction[],
  myAgentId: number | null,
): { emoji: string; count: number; mine: boolean; who: string[] }[] {
  const groups = new Map<string, { emoji: string; count: number; mine: boolean; who: string[] }>();

  for (const r of reactions) {
    let group = groups.get(r.emoji);
    if (!group) {
      group = { emoji: r.emoji, count: 0, mine: false, who: [] };
      groups.set(r.emoji, group);
    }
    group.count += 1;
    if (r.reactorKind === 1 && r.agentId != null && r.agentId === myAgentId) group.mine = true;
    group.who.push(r.reactorKind === 0 ? 'Customer' : r.agentName ?? 'An agent');
  }

  return [...groups.values()];
}

/** What this agent has on a message right now, if anything. */
export function myReaction(reactions: AppliedReaction[], myAgentId: number | null): string | null {
  if (myAgentId == null) return null;
  return reactions.find((r) => r.reactorKind === 1 && r.agentId === myAgentId)?.emoji ?? null;
}

export function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export interface AdminOverview {
  activeTenants: number;
  activeChannels: number;
  conversations: number;
  messages: number;
  messages24h: number;
  failed24h: number;
  queueDepth: number;
  deadLetters: number;
  webhookBacklog: number;
  errors24h: number;
}

export interface AdminTenant {
  id: number; slug: string; companyName: string; contactEmail: string;
  status: number; createdAt: string; balance: number | null;
  channels: number; agents: number; messages30d: number;
  /** The current subscription, or nulls for a tenant nobody has put on a plan. */
  planName: string | null; planEndsOn: string | null;
  agentsAllowed: number | null; numbersAllowed: number | null;
}

/** A row of the plan catalogue PSM sells from. */
export interface Plan {
  id: number; code: string; name: string; description: string | null;
  annualPrice: number; agentsIncluded: number; numbersIncluded: number;
  apiAccess: boolean;
  /** 0 none, 1 keyword/menu bots only, 2 bots that call the client's systems too. */
  botsLevel: 0 | 1 | 2;
  prioritySupport: boolean;
  extraAgentMonthly: number; extraNumberMonthly: number;
  /** Mobile-app seats the plan includes by default (usually 0: it is an add-on), and the price of a pack of three. */
  mobileSeats: number; mobilePackMonthly: number;
  isActive: boolean; sortOrder: number; updatedAt: string;
  /** Tenants on it right now. */
  tenantsOn: number;
}

export type PlanBody = Partial<Omit<Plan, 'id' | 'updatedAt' | 'tenantsOn'>>;

/** One term of a tenant's subscription; the allowances are the ones copied at assignment. */
export interface Subscription {
  id: number; planId: number; planName: string; annualPrice: number;
  agentsIncluded: number; numbersIncluded: number; extraAgents: number; extraNumbers: number;
  /** How many people on this contract may use the mobile app. 0 = add-on not bought. */
  mobileSeats: number;
  apiAccess: boolean; botsLevel: 0 | 1 | 2;
  startsOn: string; endsOn: string; isCurrent: boolean; notes: string | null;
  notified30dAt: string | null; notified7dAt: string | null; suspendedAt: string | null;
  createdAt: string;
}

export interface PlanUsage { agents: number; numbers: number; apiKeys: number; liveBots: number; mobileUsers: number }

export interface TenantSubscriptionView {
  current: Subscription | null;
  history: Subscription[];
  usage: PlanUsage;
  daysLeft: number | null;
}

/** What the client portal shows the team about their own plan. */
export interface MyPlan {
  plan: {
    name: string; agentsAllowed: number; numbersAllowed: number; apiAccess: boolean; botsLevel: 0 | 1 | 2;
    startsOn: string; endsOn: string; daysLeft: number;
    extraAgentMonthly: number; extraNumberMonthly: number;
    /** Seats bought; the price of one more pack of `mobilePackSize`. */
    mobileSeats: number; mobilePackMonthly: number; mobilePackSize: number;
  } | null;
  usage: PlanUsage;
}

export interface AdminChannel {
  id: number; tenantId: number; tenantName: string; wabaId: string;
  phoneNumberId: string; displayPhoneNumber: string | null; verifiedName: string | null;
  /** The client's own name for the number, set in their portal. */
  label?: string | null;
  qualityRating: string | null; messagingLimitTier: string | null;
  sendRatePerSecond: number; isActive: boolean;
  metaAppName: string; webhookSlug: string;
  /** The client still answers this number in the WhatsApp Business app on a phone. */
  isCoexistence: boolean;
  /** 0 not asked yet, 1 Meta accepted the request, 2 arriving, 3 done, 4 gave up. */
  historyState: number;
  historyMonths: number;
  historyMessages: number;
  historyError: string | null;

  /** When the number was connected. The 24-hour sync window runs from here. */
  coexistenceAt?: string | null;
  /** When Meta accepted the smb_app_data call — not when the number was connected. */
  historyRequestedAt?: string | null;
  /** Out of two. The request may not be repeated beyond that; see the sync service. */
  historyAttempts?: number;
  historyLastChunkAt?: string | null;
  historyFinishedAt?: string | null;

  /** The address-book sync, same five states as the history. */
  contactsState?: number;
  /** Names actually applied, which is not the number of entries Meta sent. */
  contactsNames?: number;
  contactsRequestedAt?: string | null;
  contactsAttempts?: number;
  contactsLastChunkAt?: string | null;
  contactsError?: string | null;

  /** Set when the handset was unlinked. Sending is off until it is reconnected. */
  disconnectedAt?: string | null;
  /** Meta's own word: ACCOUNT_DISCONNECTED, COMPANION_INACTIVITY, CHANGE_NUMBER… */
  disconnectReason?: string | null;
  /** USER or SYSTEM. Whether the client did it or Meta did it to them. */
  disconnectInitiatedBy?: string | null;
}

export const HISTORY_STATE: Record<number, string> = {
  0: 'not requested', 1: 'waiting for Meta', 2: 'arriving', 3: 'complete', 4: 'failed',
};

/**
 * Meta's window from connecting a number to asking for its contacts and history.
 *
 * Shown as a countdown in the console because the deadline is unforgiving and
 * invisible: once it passes, the only way to get either sync is for the client to
 * disconnect the number on their handset and go through Embedded Signup again.
 */
export const COEXISTENCE_SYNC_WINDOW_HOURS = 24;

/**
 * Hours left to ask Meta, or null when the number is not waiting on either sync.
 *
 * Negative means the window has closed — the caller shows that differently, because
 * "2 hours left" and "closed 3 days ago" are two different conversations to have with
 * a client and one of them cannot be fixed.
 */
export function coexistenceWindowHoursLeft(
  channel: AdminChannel,
  now: Date = new Date(),
): number | null {
  if (!channel.isCoexistence || !channel.coexistenceAt) return null;

  const waiting =
    (channel.contactsState ?? 0) === 0
    || ((channel.historyState ?? 0) === 0 && channel.historyMonths > 0);

  if (!waiting) return null;

  const closesAt = new Date(channel.coexistenceAt).getTime()
                 + COEXISTENCE_SYNC_WINDOW_HOURS * 3_600_000;

  return (closesAt - now.getTime()) / 3_600_000;
}

export interface AdminErrorEvent {
  id: number; correlationId: string | null; tenantId: number | null; tenantName: string | null;
  source: string; category: string; level: number; exceptionType: string | null;
  message: string; requestPath: string | null; occurredAt: string;
}

export interface AdminJobRun {
  id: number; jobName: string; status: number; startedAt: string;
  finishedAt: string | null; durationMs: number | null; itemsProcessed: number; detail: string | null;
}

export const MESSAGE_STATUS: Record<number, string> = {
  0: 'queued', 1: 'sent', 2: 'delivered', 3: 'read', 4: 'failed', 5: 'received',
};

export function isWindowOpen(c: Pick<Conversation, 'windowExpiresAt'>): boolean {
  return c.windowExpiresAt ? new Date(c.windowExpiresAt).getTime() > Date.now() : false;
}

export function windowRemaining(c: Pick<Conversation, 'windowExpiresAt'>): string | null {
  if (!c.windowExpiresAt) return null;
  const ms = new Date(c.windowExpiresAt).getTime() - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * A template as the picker needs it. `components` is deliberately not here: the
 * API resolves the shape at sync time, so nothing in the browser parses JSON to
 * draw a preview.
 */
export interface Template {
  id: number;
  name: string;
  language: string;
  category: string | null;
  status: string;
  bodyVariables: number;
  bodyText: string | null;
  headerText: string | null;
  footerText: string | null;
  headerFormat: string | null;
  /** Meta's sample for an image/video/document header — the file it was submitted with. Null for text headers. */
  headerSampleUrl?: string | null;
  /** Compact array for the preview, or null. Parsed once with `templateButtons`. */
  buttonsJson: string | null;
  needsHeaderMedia: boolean;
  needsButtonParams: boolean;
  rejectedReason: string | null;
  syncedAt: string | null;
  /** Present on the tenant list and single-template reads (F162). */
  needsHeaderText?: boolean;
  wabaId?: string;
  metaTemplateId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  authoredAt?: string | null;
  /** Whoever submitted it from here — an agent or platform staff. Null when synced from Meta. */
  createdBy?: string | null;
  lastUsedAt?: string | null;
  /** Only on `GET /templates/{id}`: the stored component JSON the editor reopens. */
  componentsJson?: string | null;
}

export interface TemplateButton {
  type: string;
  text: string;
  /** Meta's position in the buttons component; what a button value is addressed by. */
  index?: number;
  /** True for a dynamic-URL or copy-code button: it takes a value at send time. */
  param?: boolean;
}

/** What a template needs beyond its body values, supplied by the picker or a campaign. */
export interface TemplateSendExtras {
  /** The value for a TEXT header with {{1}} in it. */
  headerText?: string;
  /** An uploaded asset (already carrying a Meta media id) for an IMAGE/VIDEO/DOCUMENT header. */
  headerMediaAssetId?: number;
  /** Button values keyed by the button's index: a URL suffix, or a coupon code. */
  buttonParams?: Record<string, string>;
}

export function templateButtons(template: Pick<Template, 'buttonsJson'>): TemplateButton[] {
  if (!template.buttonsJson) return [];
  try {
    const parsed: unknown = JSON.parse(template.buttonsJson);
    return Array.isArray(parsed) ? (parsed as TemplateButton[]) : [];
  } catch {
    // A button we cannot read is one we do not draw. It never blocks the send.
    return [];
  }
}

/**
 * Why a template cannot be sent from this build, or null when it can.
 *
 * Returned as a sentence rather than a boolean so the picker can say what is
 * wrong instead of greying a row out with no explanation.
 */
export function templateBlockedReason(template: Template): string | null {
  if (template.status !== 'APPROVED') return `${template.status.toLowerCase()} at Meta`;
  // Media headers and button values are filled in by the picker now (F189); a
  // location header is the one thing no screen can supply.
  if (template.headerFormat === 'LOCATION') return 'has a location header';
  return null;
}

/**
 * Fills {{1}}, {{2}} … so the agent sees the message the customer will read.
 * Mirrors TemplateShape.Render on the server, including leaving an unfilled
 * placeholder visible rather than blanking it.
 */
export function renderTemplate(bodyText: string | null, variables: readonly string[]): string {
  if (!bodyText) return '';
  return bodyText.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, digits: string) => {
    const index = Number(digits);
    const value = variables[index - 1];
    return index >= 1 && value !== undefined && value !== '' ? value : match;
  });
}

/** A template as the admin list shows it — everything, including retired ones. */
export interface AdminTemplate extends Template {
  metaTemplateId: string | null;
  componentsJson: string | null;
  authoredAt: string | null;
  authoredBy: string | null;
  needsHeaderText: boolean;
}

export type TemplateButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER' | 'COPY_CODE';

export interface TemplateDraftButton {
  type: TemplateButtonType;
  text?: string;
  url?: string;
  example?: string;
  phoneNumber?: string;
}

export interface TemplateDraft {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  headerFormat: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  headerText?: string;
  headerExample?: string;
  headerMediaAssetId?: number | null;
  body: string;
  bodyExamples: string[];
  footer?: string;
  buttons: TemplateDraftButton[];

  // Authentication templates only. WhatsApp writes and translates the message
  // itself and rejects any attempt to supply one, so these three settings are
  // the whole of what an author chooses.
  addSecurityRecommendation?: boolean;
  codeExpirationMinutes?: number | null;
  otpType?: 'COPY_CODE' | 'ONE_TAP';
  otpButtonText?: string;
  otpPackageName?: string;
  otpSignatureHash?: string;
  otpAutofillText?: string;
}

export function isAuthentication(draft: Pick<TemplateDraft, 'category'>): boolean {
  return draft.category === 'AUTHENTICATION';
}

/**
 * What WhatsApp will actually send for an authentication template. The wording is
 * Meta's and is translated per language; this is the English form, shown so the
 * author can see there is nothing for them to write.
 */
export function authenticationBody(draft: TemplateDraft, code = '123456'): string {
  return draft.addSecurityRecommendation
    ? `${code} is your verification code. For your security, do not share this code.`
    : `${code} is your verification code.`;
}

export const TEMPLATE_LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'mr', label: 'Marathi' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'bn', label: 'Bengali' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'ur', label: 'Urdu' },
  { code: 'ar', label: 'Arabic' },
];

/** Meta's own caps. Mirrored here so the form can count down as someone types. */
export const TEMPLATE_LIMITS = {
  header: 60,
  body: 1024,
  footer: 60,
  buttonText: 25,
  copyCode: 20,
  url: 2000,
} as const;

/**
 * Turns an existing template back into a draft, for editing or duplicating.
 * Reads the stored components rather than the flattened columns, because that is
 * the only place the button URLs and phone numbers survive.
 */
export function templateToDraft(template: Template): TemplateDraft {
  const draft: TemplateDraft = {
    name: template.name,
    language: template.language,
    category: (template.category?.toUpperCase() as TemplateDraft['category']) ?? 'UTILITY',
    headerFormat: (template.headerFormat as TemplateDraft['headerFormat']) ?? 'NONE',
    headerText: template.headerText ?? undefined,
    body: template.bodyText ?? '',
    bodyExamples: Array.from({ length: template.bodyVariables }, () => ''),
    footer: template.footerText ?? undefined,
    buttons: [],
  };

  if (!template.componentsJson) return draft;

  try {
    const components: unknown = JSON.parse(template.componentsJson);
    if (!Array.isArray(components)) return draft;

    for (const component of components as Array<Record<string, unknown>>) {
      const type = String(component.type ?? '').toUpperCase();

      if (type === 'HEADER') {
        const example = component.example as { header_text?: string[] } | undefined;
        if (example?.header_text?.[0]) draft.headerExample = example.header_text[0];
      }

      if (type === 'BODY') {
        const example = component.example as { body_text?: string[][] } | undefined;
        const samples = example?.body_text?.[0];
        if (samples) {
          draft.bodyExamples = draft.bodyExamples.map((v, i) => samples[i] ?? v);
        }
      }

      if (type === 'BUTTONS' && Array.isArray(component.buttons)) {
        draft.buttons = (component.buttons as Array<Record<string, unknown>>).map((b) => ({
          type: String(b.type ?? 'QUICK_REPLY').toUpperCase() as TemplateButtonType,
          text: b.text as string | undefined,
          url: b.url as string | undefined,
          phoneNumber: b.phone_number as string | undefined,
          example: Array.isArray(b.example) ? String(b.example[0]) : (b.example as string | undefined),
        }));
      }
    }
  } catch {
    // A template we cannot read back opens as a blank draft rather than throwing
    // the editor away. The list still shows it correctly.
  }

  return draft;
}

export interface Tag {
  id: number;
  name: string;
  colour: string | null;
  conversationCount: number;
}

/** A tag as it arrives on a conversation — no usage count, it is not needed there. */
export interface AppliedTag {
  id: number;
  name: string;
  colour: string | null;
}

/**
 * Readable text for a tag chip, chosen from the background's luminance.
 *
 * The stored colour is whoever picked it's choice, and a pale one with white
 * text is unreadable. Deciding here means a bad colour is still a legible chip
 * rather than a support question.
 */
export function tagTextColour(colour: string | null): string {
  if (!colour || colour.length !== 7) return '#FFFFFF';

  const r = parseInt(colour.slice(1, 3), 16);
  const g = parseInt(colour.slice(3, 5), 16);
  const b = parseInt(colour.slice(5, 7), 16);
  if ([r, g, b].some(Number.isNaN)) return '#FFFFFF';

  // Rec. 601 luma — close enough for a chip, and cheap.
  return (r * 299 + g * 587 + b * 114) / 1000 > 145 ? '#1A1D23' : '#FFFFFF';
}

/**
 * The palette offered when creating a tag.
 *
 * A step lighter than it was. The chip renders the colour as a wash rather than a
 * fill now, and near-black source colours (#0F766E, #1D4ED8) produce a grey-looking
 * wash and text too dark to tell apart at chip size. These are the same hues one
 * step up in lightness, which keeps eight of them distinguishable when each is
 * only twelve per cent of the surface.
 */
export const TAG_COLOURS = [
  '#E2603A', '#D08A1E', '#2E9E4F', '#159A8C',
  '#3B7BE8', '#8558E0', '#D4488A', '#6B7A88',
] as const;

export interface Note {
  id: number;
  body: string;
  agentId: number;
  agentName: string | null;
  createdAt: string;
  /** Set once the note has been rewritten. Null means what it says is what was written. */
  editedAt: string | null;
  mentionedAgentIds: number[];
}

/**
 * One entry in the thread. A note sits in the same timeline as the messages
 * because it is almost always about the message above it — read anywhere else,
 * the reader has to work out what it referred to from its timestamp.
 */
export type ThreadEntry =
  | { kind: 'message'; at: number; message: Message }
  | { kind: 'note'; at: number; note: Note }
  | { kind: 'event'; at: number; event: ConversationEvent };

export function buildThread(
  messages: Message[], notes: Note[], events: ConversationEvent[] = [],
): ThreadEntry[] {
  const entries: ThreadEntry[] = [
    ...messages.map((message) => ({
      kind: 'message' as const, at: new Date(message.createdAt).getTime(), message,
    })),
    ...notes.map((note) => ({
      kind: 'note' as const, at: new Date(note.createdAt).getTime(), note,
    })),
    // The system pills. Interleaved by time like everything else, because "the
    // field was set, then she replied" and "she replied, then the field was set"
    // are different stories and the timeline is the only thing that tells them
    // apart.
    ...events.map((event) => ({
      kind: 'event' as const, at: new Date(event.createdAt).getTime(), event,
    })),
  ];

  return entries.sort((a, b) => a.at - b.at);
}

/**
 * How a number is named on screen: the team's label first, then Meta's display
 * name, then the number itself — and the number after any name, because two
 * numbers on one account usually carry the same company name.
 */
export function channelName(c: { label?: string | null; verifiedName?: string | null; displayPhoneNumber: string | null }): string {
  const name = c.label ?? c.verifiedName;
  const number = c.displayPhoneNumber ?? '';
  if (!name) return number || 'This number';
  return number ? `${name} · ${number}` : name;
}

/** The short form: the team's label, or Meta's display name, or the number. */
export function channelShortName(c: { label?: string | null; verifiedName?: string | null; displayPhoneNumber: string | null }): string {
  return c.label ?? c.verifiedName ?? c.displayPhoneNumber ?? 'This number';
}

export interface CannedReply {
  id: number;
  shortcut: string;
  body: string;
  createdAt: string;
  /** A file sent with the text, uploaded once and cloned to whichever number the chat is on. */
  mediaAssetId: number | null;
  mediaFileName: string | null;
  mediaMimeType: string | null;
  /** Signed, short-lived link to the file, for a thumbnail. Null when there is no file or it is not stored. */
  mediaUrl?: string | null;
}

/** What a quick reply's placeholders are filled from. Anything missing is left visible. */
export interface QuickReplyContext {
  contactName: string | null;
  agentName?: string | null;
  /** The contact's custom fields, by key — for {{field.key}}. */
  fields?: Record<string, string> | null;
}

/**
 * Fills a quick reply's placeholders: {{name}}, {{first_name}}, {{agent}},
 * {{field.key}} for a custom field. Anything the context cannot supply — a
 * contact with no name, a field that is empty, a placeholder nobody defined
 * ({{order_no}}) — is left visible rather than blanked: the text lands in the
 * composer where the agent reads it before sending, so a visible {{…}} gets
 * filled by hand and a silently empty one does not.
 */
export function fillQuickReply(body: string, context: QuickReplyContext | string | null): string {
  const ctx: QuickReplyContext = typeof context === 'string' || context === null ? { contactName: context } : context;
  const name = ctx.contactName?.trim() || null;
  const first = name ? name.split(/\s+/)[0] ?? null : null;
  const agent = ctx.agentName?.trim() || null;

  return body.replace(/\{\{\s*([a-zA-Z_][\w.-]*)\s*\}\}/g, (match, key: string) => {
    const k = key.toLowerCase();
    if (k === 'name') return name ?? match;
    if (k === 'first_name' || k === 'firstname') return first ?? match;
    if (k === 'agent' || k === 'agent_name') return agent ?? match;
    if (k.startsWith('field.')) {
      const value = ctx.fields?.[key.slice('field.'.length)] ?? ctx.fields?.[k.slice('field.'.length)];
      return value && value.trim() ? value : match;
    }
    return match;
  });
}

/** The placeholders still left in a filled quick reply — what the agent has to type. */
export function unfilledPlaceholders(text: string): string[] {
  return [...text.matchAll(/\{\{\s*([a-zA-Z_][\w.-]*)\s*\}\}/g)].map((m) => m[1]!);
}

// ---------------------------------------------------------------- assignment --

export interface AgentGroup {
  id: number;
  name: string;
  members: { agentId: number; displayName: string; available: boolean }[];
}

export interface AssignmentRule {
  id: number;
  name: string;
  priority: number;
  isActive: boolean;
  matchChannelId: number | null;
  matchChannelLabel: string | null;
  matchTagId: number | null;
  matchTagLabel: string | null;
  matchKeyword: string | null;
  targetGroupId: number | null;
  targetGroupLabel: string | null;
}

/** What a rule matches on, in one word, for the list. */
export function ruleCondition(rule: AssignmentRule): string {
  if (rule.matchChannelId !== null) return `Sent to ${rule.matchChannelLabel ?? 'a number'}`;
  if (rule.matchTagId !== null) return `Tagged ${rule.matchTagLabel ?? 'a tag'}`;
  if (rule.matchKeyword) return `Message contains \u201c${rule.matchKeyword}\u201d`;
  return 'Everything else';
}

/**
 * The mistake the rule list has to warn about.
 *
 * A catch-all placed above a specific rule swallows it: first match wins, and a
 * rule with no condition matches everything. It is not an error — the tenant may
 * have meant it — so it is a warning on the row rather than a rejected save.
 */
export function shadowedRuleIds(rules: AssignmentRule[]): Set<number> {
  const shadowed = new Set<number>();
  const ordered = [...rules]
    .filter((r) => r.isActive)
    .sort((a, b) => a.priority - b.priority || a.id - b.id);

  const catchAllAt = ordered.findIndex(
    (r) => r.matchChannelId === null && r.matchTagId === null && !r.matchKeyword,
  );

  if (catchAllAt >= 0) {
    for (const rule of ordered.slice(catchAllAt + 1)) shadowed.add(rule.id);
  }

  return shadowed;
}

// ---------------------------------------------------------------------- team --

export interface TeamMember {
  id: number;
  displayName: string;
  email: string;
  /** The role's name, or null when the role it was on has been deleted. */
  role: string | null;
  roleId: number | null;
  supervisorId: number | null;
  /** Comma-separated channel ids, or null meaning every number. */
  channelIds: string | null;
  isActive: boolean;
  /** 0 Away, 1 Available. */
  availability: number;
  lastLoginAt: string | null;
  openConversations: number;
  /** Holds one of the tenant's mobile-app seats. */
  mobileAccess: boolean;
  /** Phones signed in and not cut off. */
  mobileDevices: number;
}

/** One phone an agent has signed in from. `deviceId` is opaque, minted by the app. */
export interface MobileDevice {
  deviceId: string;
  platform: 'android' | 'ios';
  model: string | null;
  osVersion: string | null;
  appVersion: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  revokedAt: string | null;
  /** A live session exists for this phone right now. */
  signedIn: boolean;
}

export interface PermissionDefinition {
  key: string;
  group: string;
  label: string;
  detail: string;
}

export interface Role {
  id: number;
  name: string;
  description: string | null;
  isSystem: boolean;
  agentCount: number;
  permissions: string[];
}

export interface TenantSettings {
  timeZone: string;
  hoursEnabled: boolean;
  awayMessage: string | null;
  holdAssignment: boolean;
  /**
   * The template the composer uses to reopen a closed window.
   *
   * Null when none is nominated **and** when the nominated one has since been
   * deleted or unapproved in Meta's console — the server joins rather than
   * echoing the column, so the composer falls back to the picker and says so
   * instead of sending an id that fails.
   */
  sessionOpenerTemplateId: number | null;

  /** While a flow is live on a conversation, routing waits for it. */
  botOwnsUntilHandoff: boolean;

  /**
   * Who may be given a new chat.
   *
   * 0 only people who are Available · 1 Available first, then anyone active ·
   * 2 anyone active, ignoring the Available/Away toggle entirely.
   *
   * 1 and 2 both mean a chat can land on somebody who is not at their desk — and
   * an assigned chat leaves the Unassigned queue the whole team watches. That is
   * the trade, and it is why 0 is the default.
   */
  assignmentPool: 0 | 1 | 2;

  /** 0 off · 1 the first time a chat reaches a person in a window · 2 every change of agent. */
  assignmentNoticeMode: 0 | 1 | 2;
  /** Sent inside the 24-hour window. `{{agent}}` is substituted. */
  assignmentNoticeText: string | null;
  /** Sent outside it. Null also means "nominated but no longer approved in Meta". */
  assignmentNoticeTemplateId: number | null;

  /** 0 nothing · 1 the custom text · 2 the working-hours away message. */
  noAgentNoticeMode: 0 | 1 | 2;
  noAgentNoticeText: string | null;
}

/** One flow, as the routing screen needs to know about it. */
export interface RoutingFlow {
  id: number;
  name: string;
  isEnabled: boolean;
  channelId: number | null;
  channelName: string | null;
  /**
   * Whether the flow contains a handoff step. False means it can never reach a
   * person on its own — with bot-first ordering the rules are never asked, and
   * every conversation waits in Unassigned for somebody to open it.
   */
  hasHandoff: boolean;
}

export interface WorkingHour {
  id: number;
  channelId: number | null;
  dayOfWeek: number;
  opensAt: string;
  closesAt: string;
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Can this member be demoted or deactivated without stranding the tenant?
 *
 * Mirrors the server's guard so the UI can grey the control and say why, rather
 * than offering it and reporting a 409. The server check is the real one — this is
 * only the explanation.
 */
export function isLastAdministrator(team: TeamMember[], member: TeamMember, roles: Role[]): boolean {
  // "Administrator" is no longer a role name, it is whoever can manage the team.
  // A tenant that renamed the idea, or split it across two composed roles, must not
  // be told it is about to strand itself when it is not.
  const managing = new Set(roles.filter((r) => r.permissions.includes('team.manage')).map((r) => r.id));

  if (!member.isActive || member.roleId === null || !managing.has(member.roleId)) return false;

  return team.filter((m) => m.isActive && m.roleId !== null && managing.has(m.roleId)).length === 1;
}

// -------------------------------------------------------------- provisioning --

export interface MetaApp {
  id: number;
  name: string;
  metaAppId: string;
  webhookSlug: string;
  graphVersion: string;
  isActive: boolean;
  /** Null means every client may route through it; a value means exactly one may. */
  tenantId: number | null;
  tenantName: string | null;
  hasAppSecret: number;
  channels: number;
  /** Meta's Login-for-Business configuration id. Null until the app is an approved Tech Provider. */
  esConfigId: string | null;
}

export interface ChannelCheck {
  ok: boolean;
  problem: string | null;
  verifiedName: string | null;
  displayPhoneNumber: string | null;
  qualityRating: string | null;
  throughputLevel: string | null;
  suggestedRatePerSecond: number;
  /** Meta's state of the number (CONNECTED, PENDING, …); null when the check failed. */
  status?: string | null;
  /** The display-name review (APPROVED, PENDING_REVIEW, DECLINED…), separate from being usable. */
  nameStatus?: string | null;
  /** False: every send answers 133010 until the number is registered with a two-step PIN. */
  registered?: boolean | null;
}

/** Which apps a given client may route through: the platform's, plus their own. */
export function appsAvailableTo(apps: MetaApp[], tenantId: number | null): MetaApp[] {
  return apps.filter((a) => a.isActive && (a.tenantId === null || a.tenantId === tenantId));
}

// ----------------------------------------------------------------- schedule --

export interface ScheduledMessage {
  id: number;
  scheduledFor: string;
  messageId: number;
  conversationId: number;
  messageType: string;
  body: string | null;
  contactName: string | null;
  waId: string;
  scheduledBy: string | null;
  windowExpiresAt: string | null;
}

/**
 * Will this probably be refused when it fires?
 *
 * A warning, never a refusal. The window may well be open again by then, because
 * customers reply — and a tool that forbids something likely to work is worse than
 * one that mentions it. A template is exempt: that is the thing that works outside
 * the window, and it is what the warning points at.
 */
export function scheduledPastWindow(
  scheduledFor: string,
  windowExpiresAt: string | null,
  messageType = 'text',
): boolean {
  if (messageType === 'template') return false;
  if (!windowExpiresAt) return true;
  return new Date(scheduledFor).getTime() > new Date(windowExpiresAt).getTime();
}

// ----------------------------------------------------------------- contacts --

export interface Contact {
  id: number;
  waId: string;
  displayName: string | null;
  /** The custom-field bag, as the server stores it: JSON text, or null. */
  attributes: string | null;
  optedOutAt: string | null;
  optOutReason: string | null;
  optOutMessage?: string | null;
  optOutBy?: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  /** On the Customers list only: every tag on any of this person's conversations, comma-joined. */
  tagNames?: string | null;
  blockedAt?: string | null;
}

/** A list a contact is on — static by membership, rule-based by answering the rule. */
export interface ContactSegment {
  id: number;
  name: string;
  kind: 0 | 1;
}

export interface ContactField {
  id: number;
  fieldKey: string;
  label: string;
  fieldType: 'text' | 'number' | 'date' | 'list' | 'boolean';
  options: string | null;
  sortOrder: number;
}

/**
 * The bag, parsed, without throwing.
 *
 * A contact whose attributes are unreadable still has a phone number and a name,
 * and the screen has to render them. Returning {} loses one cell; throwing loses
 * the row and, in a list, every row after it.
 */
export function contactAttributes(contact: Pick<Contact, 'attributes'>): Record<string, string> {
  if (!contact.attributes) return {};
  try {
    const parsed: unknown = JSON.parse(contact.attributes);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    );
  } catch {
    return {};
  }
}

/** The options of a list field, which the server stores as a JSON array. */
export function fieldOptions(field: ContactField): string[] {
  if (!field.options) return [];
  try {
    const parsed: unknown = JSON.parse(field.options);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

// ----------------------------------------------------------------- segments --

/** One clause. The server ANDs them; there is no OR, by design. */
export interface SegmentRule {
  kind: 'name' | 'phone' | 'tag' | 'field' | 'opted_out' | 'last_inbound' | 'created' | 'channel';
  op: string;
  field?: string | null;
  value?: string | null;
  /** Rules in the same group are ANDed; groups are ORed. Absent means 0. */
  group?: number;
}

export interface Segment {
  id: number;
  name: string;
  /** 0 static — a fixed list. 1 dynamic — a saved question. */
  kind: 0 | 1;
  rules: string | null;
  createdAt: string;
  createdBy: string | null;
  /** Null for a dynamic segment: counting one costs a query, so it is asked for. */
  memberCount: number | null;
}

export interface SegmentMember {
  id: number;
  waId: string;
  displayName: string | null;
  optedOutAt?: string | null;
  blockedAt?: string | null;
  /** Set when a message to this number last failed with 131026; cleared by a delivery or a reply. */
  notOnWhatsAppAt?: string | null;
  lastInboundAt?: string | null;
  /** When they joined a fixed list; when the contact was created for a rule-based one. */
  addedAt?: string | null;
}

/** Who the members panel shows. `reachable` is who a send would reach — the default. */
export type MemberFilter = 'reachable' | 'all' | 'optedOut' | 'blocked' | 'noWhatsapp' | 'neverReplied';

export const MEMBER_FILTER_LABEL: Record<MemberFilter, string> = {
  reachable: 'Reachable',
  all: 'Everyone',
  optedOut: 'Opted out',
  blocked: 'Blocked',
  noWhatsapp: 'No WhatsApp',
  neverReplied: 'Never replied',
};

export interface MemberQuery {
  filter?: MemberFilter;
  q?: string | null;
  tag?: number | null;
  /** yyyy-mm-dd, inclusive. */
  from?: string | null;
  to?: string | null;
}

export type MemberAction = 'remove' | 'copy' | 'move' | 'clearNoWhatsapp';

export type RecipientAction = 'removeFromList' | 'addToList' | 'markNoWhatsapp';

export function segmentRules(segment: Pick<Segment, 'rules'>): SegmentRule[] {
  if (!segment.rules) return [];
  try {
    const parsed: unknown = JSON.parse(segment.rules);
    return Array.isArray(parsed) ? (parsed as SegmentRule[]) : [];
  } catch {
    return [];
  }
}

// ------------------------------------------------------------------ imports --

export interface ImportJob {
  id: number;
  fileName: string;
  /** 0 pending, 1 running, 2 done, 3 failed. */
  status: 0 | 1 | 2 | 3;
  totalRows: number;
  processed: number;
  imported: number;
  updated: number;
  skipped: number;
  lastError: string | null;
  createdAt: string;
  completedAt: string | null;
  startedBy?: string | null;
}

export interface ImportProblem {
  lineNumber: number;
  reason: string;
  raw: string | null;
}

export interface ImportPreview {
  headers: string[];
  sample: string[][];
  totalRows: number;
  /** The file itself, handed back so the confirm step does not re-upload it. */
  content: string;
}

/**
 * A guess at which CSV column is which field.
 *
 * Only a guess: it is the starting position of a mapping screen the user can
 * change, not a decision. Getting it right for the common header names is the
 * difference between an import that takes four clicks and one that takes twelve.
 */
export function guessMapping(headers: string[], fields: ContactField[]): Record<string, number> {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const mapping: Record<string, number> = {};

  const find = (candidates: string[]) =>
    headers.findIndex((h) => candidates.includes(norm(h)));

  const phone = find(['phone', 'phonenumber', 'mobile', 'mobileno', 'whatsapp', 'whatsappnumber', 'number', 'contact', 'msisdn', 'waid']);
  if (phone >= 0) mapping.phone = phone;

  const name = find(['name', 'fullname', 'firstname', 'displayname', 'customername', 'contactname']);
  if (name >= 0) mapping.name = name;

  for (const field of fields) {
    const index = headers.findIndex((h) => norm(h) === norm(field.label) || norm(h) === norm(field.fieldKey));
    if (index >= 0) mapping[field.fieldKey] = index;
  }

  return mapping;
}

// ---------------------------------------------------------------- campaigns --

/** Where one template variable's value comes from. */
export interface VariableBinding {
  source: 'name' | 'field' | 'literal';
  key?: string | null;
  value?: string | null;
  /** Used when the contact has nothing for this binding. Never empty. */
  fallback: string;
}

export type CampaignStatus = 0 | 1 | 2 | 3 | 4 | 5;

export interface Campaign {
  id: number;
  name: string;
  /** 0 draft, 1 scheduled, 2 running, 3 paused, 4 completed, 5 cancelled. */
  status: CampaignStatus;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  deliveredCount: number;
  readCount: number;
  clickedCount?: number;
  /** Recipients with an inbound message after the broadcast reached them. */
  repliedCount?: number;
  segmentId?: number | null;
  templateStatus?: string | null;
  templateBody?: string | null;
  /** NONE, TEXT, IMAGE, VIDEO or DOCUMENT — what sits above the body. */
  templateHeaderFormat?: string | null;
  /** The campaign's own header text when it set one, else the template's. */
  templateHeaderText?: string | null;
  templateFooter?: string | null;
  templateButtonsJson?: string | null;
  /** The sample Meta approved the template with; what the bubble shows when the campaign has no file of its own. */
  templateHeaderSampleUrl?: string | null;
  headerMediaAssetId?: number | null;
  headerFileName?: string | null;
  headerMimeType?: string | null;
  /** Signed, short-lived; pass through apiUrl(). Null until the file is stored. */
  headerMediaUrl?: string | null;
  estimatedCost: number | null;
  /** wallet | tier | agent. */
  pauseReason: string | null;
  /** Set when the number has hit its 24-hour ceiling and the rest waits. */
  resumeAfter: string | null;
  createdAt: string;
  templateName: string;
  fromNumber: string | null;
  createdBy?: string | null;
}

export interface CampaignDetail extends Campaign {
  channelId: number;
  templateId: number;
  segmentId: number | null;
  retargetOfCampaignId: number | null;
  retargetOutcome: string | null;
  variableMap: string | null;
  frozenAt: string | null;
  templateBody: string | null;
  bodyVariables: number;
  templateStatus: string | null;
  templateNeedsHeaderMedia?: boolean;
  templateNeedsHeaderText?: boolean;
  /** The value for a TEXT header's {{1}}. */
  headerText?: string | null;
}

export interface CampaignClickSummary {
  buttonPayload: string;
  buttonText: string | null;
  clicks: number;
}

export interface CampaignEstimate {
  recipients: number;
  unitPrice: number;
  cost: number;
  balance: number;
  affordable: boolean;
  problem: string | null;
}

export interface CampaignPreview {
  contact: { id: number; waId: string; displayName: string | null } | null;
  values: string[];
  /** How many values came from the fallback rather than from the contact. */
  usedFallback: number;
  body: string;
}

export interface CampaignRecipient {
  id: number;
  contactId: number;
  waId: string | null;
  /** 0 pending, 1 queued, 2 sent, 3 failed, 4 skipped. */
  status: 0 | 1 | 2 | 3 | 4;
  failureReason: string | null;
  processedAt: string | null;
  displayName: string | null;
  /** From the message: 2 delivered, 3 read, 4 failed. Null until it is sent. */
  messageStatus: number | null;
  errorTitle: string | null;
  /** The chat the message went out on. Null until it is sent. */
  conversationId: number | null;
  /** When the first message came in after the broadcast reached them. Null if none did. */
  repliedAt: string | null;
  notOnWhatsAppAt?: string | null;
}

export const CAMPAIGN_STATUS: Record<CampaignStatus, string> = {
  0: 'Draft',
  1: 'Scheduled',
  2: 'Sending',
  3: 'Paused',
  4: 'Sent',
  5: 'Cancelled',
};

/**
 * Two maps that mean the same thing, compared as such.
 *
 * The server stores every binding with all four keys (`"key":null` written out);
 * the builder's fresh default is `{ source, fallback }`. `JSON.stringify` says those
 * differ, which kept "Save draft" looking unsaved — and Send disabled — until the
 * page was reloaded. Compare the meaning, not the spelling.
 */
export function sameVariableMap(a: VariableBinding[], b: VariableBinding[]): boolean {
  const canon = (m: VariableBinding[]) => JSON.stringify(m.map((x) => [x.source, x.key ?? null, x.value ?? null, x.fallback ?? '']));
  return canon(a) === canon(b);
}

export function parseVariableMap(json: string | null | undefined): VariableBinding[] {
  if (!json) return [];
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as VariableBinding[]) : [];
  } catch {
    return [];
  }
}

/**
 * Why this campaign cannot be launched yet, or null.
 *
 * The same rules the server enforces, said before the button is pressed rather
 * than after. The server is still the authority — this only saves a round trip
 * and, more usefully, explains what to fix while the form is still open.
 */
export function campaignBlockers(
  map: VariableBinding[],
  bodyVariables: number,
  hasAudience: boolean,
  templateStatus: string | null,
): string[] {
  const problems: string[] = [];

  if (!hasAudience) problems.push('Choose who this goes to.');

  if (templateStatus && templateStatus.toUpperCase() !== 'APPROVED') {
    problems.push(`This template is ${templateStatus.toLowerCase()} and cannot be sent.`);
  }

  if (map.length !== bodyVariables) {
    problems.push(`This template takes ${bodyVariables} value${bodyVariables === 1 ? '' : 's'}.`);
    return problems;
  }

  map.forEach((binding, i) => {
    if (!binding.fallback?.trim()) {
      problems.push(`Value ${i + 1} needs a fallback for contacts that have nothing to put there.`);
    }
    if (binding.source === 'field' && !binding.key) {
      problems.push(`Value ${i + 1} is set to a contact field but names none.`);
    }
    if (binding.source === 'literal' && !binding.value?.trim()) {
      problems.push(`Value ${i + 1} is a fixed value, which cannot be blank.`);
    }
  });

  return problems;
}

/** The variable positions a template body actually uses, in order: {{1}}, {{2}}… */
export function templateVariablePositions(body: string | null | undefined): number[] {
  if (!body) return [];
  const found = new Set<number>();
  for (const match of body.matchAll(/\{\{(\d+)\}\}/g)) found.add(Number(match[1]));
  return [...found].sort((a, b) => a - b);
}

// --------------------------------------------------------------------- bots --

export interface BotFlowSummary {
  id: number;
  name: string;
  version: number;
  isPublished: boolean;
  isEnabled: boolean;
  channelId: number | null;
  channelNumber: string | null;
  /** The version this one was opened from when that was published; null for a first version. */
  publishedFromId?: number | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** How many conversations are mid-way through this exact version right now. */
  liveSessions: number;
  /** When this bot answers: 'keyword' | 'outside_hours' | 'always'. */
  triggerKind?: BotTriggerKind;
  /** For 'keyword': the words, comma-separated. */
  triggerValue?: string | null;
  /** Lowest first — the order the engine tries live bots in. */
  priority?: number;
  /** Whether it may take over a conversation another bot is mid-way through. */
  canInterrupt?: boolean;
}

export type BotTriggerKind = 'keyword' | 'outside_hours' | 'always';

/** What the bots screen sends when the entry condition changes. */
export interface BotEntry {
  triggerKind?: BotTriggerKind;
  triggerValue?: string | null;
  priority?: number;
  canInterrupt?: boolean;
}

export const BOT_TRIGGERS: { value: BotTriggerKind; label: string; hint: string }[] = [
  { value: 'keyword', label: 'When somebody says…', hint: 'One or more words, separated by commas. Matched anywhere in the message.' },
  { value: 'outside_hours', label: 'Outside working hours', hint: 'Uses Settings → Working hours. A tenant with hours switched off is always open, so this never fires.' },
  { value: 'always', label: 'Anything else', hint: 'The catch-all. Put it last, or it answers everything.' },
];

/**
 * The entry condition as a sentence, for the line that says which bot answers
 * what. Deliberately the customer's point of view, not the column names.
 */
export function describeEntry(flow: Pick<BotFlowSummary, 'triggerKind' | 'triggerValue'>): string {
  if (flow.triggerKind === 'keyword') {
    const words = (flow.triggerValue ?? '').split(',').map((w) => w.trim()).filter(Boolean);
    return words.length === 0 ? 'when somebody says a word you have not set yet'
      : `when somebody says ${words.map((w) => `“${w}”`).join(' or ')}`;
  }
  if (flow.triggerKind === 'outside_hours') return 'outside your working hours';
  return 'to anything another bot has not already taken';
}

/**
 * One bot, as the person sees it: every version that descends from one first
 * version, grouped. The rows the API returns are versions — v1 published, v2 the
 * draft opened from it — and listing them flat put the same name on two lines with
 * nothing to say which one to open.
 */
export interface BotGroup {
  /** The first version's id — stable for the bot's whole life. */
  key: string;
  name: string;
  versions: BotFlowSummary[];
  draft?: BotFlowSummary;
  /** The newest published version, live or not. */
  published?: BotFlowSummary;
  live?: BotFlowSummary;
  channelNumber: string | null;
  liveSessions: number;
}

export function groupBotVersions(rows: BotFlowSummary[]): BotGroup[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const rootOf = (row: BotFlowSummary): string => {
    // Older API builds do not send the parent; fall back to the name, which
    // publishing copies unchanged.
    if (row.publishedFromId === undefined) return `name:${row.name}|${row.channelId ?? ''}`;
    let current = row;
    const seen = new Set<number>();
    while (current.publishedFromId != null && byId.has(current.publishedFromId) && !seen.has(current.id)) {
      seen.add(current.id);
      current = byId.get(current.publishedFromId)!;
    }
    return `id:${current.id}`;
  };

  const groups = new Map<string, BotFlowSummary[]>();
  for (const row of rows) {
    const key = rootOf(row);
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }

  return [...groups.entries()].map(([key, versions]) => {
    versions.sort((a, b) => b.version - a.version);
    const draft = versions.find((v) => !v.isPublished);
    const published = versions.find((v) => v.isPublished);
    const live = versions.find((v) => v.isEnabled);
    const head = draft ?? versions[0]!;
    return {
      key,
      name: head.name,
      versions,
      draft,
      published,
      live,
      channelNumber: (live ?? published ?? head).channelNumber,
      liveSessions: versions.reduce((n, v) => n + v.liveSessions, 0),
    };
  }).sort((a, b) =>
    // Live bots first, and among them the order the engine reads them in — which
    // is the only order that answers "who replies to this?".
    Number(Boolean(b.live)) - Number(Boolean(a.live))
    || (a.live && b.live ? (a.live.priority ?? 100) - (b.live.priority ?? 100) : 0)
    || a.name.localeCompare(b.name));
}

export interface BotFlow {
  id: number;
  tenantId: number;
  name: string;
  version: number;
  isPublished: boolean;
  isEnabled: boolean;
  channelId: number | null;
  definition: string;
  publishedAt: string | null;
  updatedAt: string | null;
}

export interface FlowReport {
  ok: boolean;
  errors: string[];
  warnings: string[];
}

export interface BotAction {
  id: number;
  actionKey: string;
  name: string;
  description: string | null;
  config: string;
  authType: string | null;
  authHeader: string | null;
  /** The credential itself is never sent to a browser. This only says whether one exists. */
  hasSecret: boolean;
  isActive: boolean;
  updatedAt: string;
}

/**
 * What happened to the conversations a bot handled.
 *
 * `leftEarly` is the figure that matters and the one nothing could answer before: a
 * conversation that ended because the customer stopped replying, or because the bot
 * gave up on an answer it could not understand. A flow reaching its own end step is
 * `completed` and is not a drop-off.
 */
export interface BotFunnel {
  name: string;
  days: number;
  total: number;
  /** Every outcome by its own name, so one added later is not folded into "other". */
  byOutcome: Record<string, number>;
  completed: number;
  handedOff: number;
  leftEarly: number;
  /** Median rather than mean: one looping conversation would drag an average. */
  medianTurns: number;
  /** Where customers stopped, commonest first. */
  dropOff: { node: string; count: number }[];
  /** How many conversations got as far as each step, counted once each. */
  reached: { node: string; count: number }[];
}

export interface BotSessionRow {
  conversationId: number;
  state: string;
  variables: string | null;
  stepCount: number;
  updatedAt: string;
  handedOffAt: string | null;
  flowName: string;
  flowVersion: number;
  contactName: string | null;
  waId: string;
}

/** Every node type the interpreter understands. */
export const FLOW_NODE_TYPES = [
  'message', 'list', 'buttons', 'products', 'capture', 'condition',
  'action', 'set', 'dynamic_list', 'handoff', 'end',
  // Added after the bot audit: a template is the only thing that reaches a customer
  // whose 24-hour window has closed, and the rest were each a thing a flow author
  // asked for and had to fake with an action node or do without.
  'template', 'tag', 'wait', 'jump',
] as const;

export type FlowNodeType = (typeof FLOW_NODE_TYPES)[number];

export interface FlowDefinition {
  start?: string;
  triggers?: string[];
  resume?: Record<string, unknown>;
  nodes?: Record<string, FlowNode>;
}

export interface FlowNode {
  type?: FlowNodeType;
  [key: string]: unknown;
}

export function parseFlow(json: string | null | undefined): FlowDefinition {
  if (!json) return { nodes: {} };
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as FlowDefinition)
      : { nodes: {} };
  } catch {
    return { nodes: {} };
  }
}

/**
 * Every node one node can lead to, however it leads there.
 *
 * The same set the server walks for reachability. Kept in step deliberately: the map
 * drawing different edges from the ones the validator checks would make an
 * unreachable node look connected.
 */
export function flowTargets(node: FlowNode | undefined): string[] {
  if (!node) return [];

  const targets: unknown[] = [node.next, node.default, node.empty_next];

  for (const key of ['transitions', 'cases'] as const) {
    const map = node[key];
    if (map && typeof map === 'object') targets.push(...Object.values(map as Record<string, unknown>));
  }

  if (Array.isArray(node.extra_rows)) {
    for (const extra of node.extra_rows as Record<string, unknown>[]) targets.push(extra?.target);
  }

  return targets.filter((t): t is string => typeof t === 'string' && t.length > 0);
}

/**
 * Lays the flow out in columns by distance from the start.
 *
 * A breadth-first walk rather than a physics simulation: a flow is a directed graph
 * that people read top to bottom, and a force-directed layout of the same 64 nodes
 * puts them in a cloud nobody can follow. Anything unreachable is placed in its own
 * column at the end, which is exactly the thing you want to see.
 */
export function layoutFlow(definition: FlowDefinition): {
  nodes: { id: string; column: number; row: number; node: FlowNode; unreachable: boolean }[];
  edges: { from: string; to: string; label?: string }[];
  columns: number;
} {
  const nodes = definition.nodes ?? {};
  const ids = Object.keys(nodes);
  const start = definition.start && nodes[definition.start] ? definition.start : ids[0];

  const depth = new Map<string, number>();
  if (start) {
    const queue: [string, number][] = [[start, 0]];
    while (queue.length > 0) {
      const [id, d] = queue.shift()!;
      if (depth.has(id)) continue;
      depth.set(id, d);
      for (const target of flowTargets(nodes[id])) {
        if (nodes[target] && !depth.has(target)) queue.push([target, d + 1]);
      }
    }
  }

  const maxDepth = depth.size > 0 ? Math.max(...depth.values()) : 0;

  const rowsPerColumn = new Map<number, number>();
  const reachable = ids.filter((id) => depth.has(id));

  const placed = reachable.map((id) => {
    const column = depth.get(id)!;
    const row = rowsPerColumn.get(column) ?? 0;
    rowsPerColumn.set(column, row + 1);
    return { id, column, row, node: nodes[id]!, unreachable: false };
  });

  // Unreachable nodes go in the first column, below everything else — not in a
  // column of their own past the end. Finding what nothing points at is the main
  // reason to look at the map at all, and parking it off the right-hand edge of a
  // sixty-node diagram means scrolling past the entire flow to discover it.
  const deepestRow = Math.max(0, ...placed.map((n) => n.row + 1));
  ids.filter((id) => !depth.has(id)).forEach((id, i) => {
    placed.push({ id, column: 0, row: deepestRow + i, node: nodes[id]!, unreachable: true });
  });

  // One line per pair, not one per way of getting there.
  //
  // A condition with two cases leading to the same step, or a menu with two options
  // that do, is one connection on the page — drawing it twice lays identical curves
  // on top of each other, which darkens the stroke and reads as a stronger link, and
  // makes the caption claim more connections than the flow has. The imported Carysil
  // flow has 104 ways to move between 98 pairs of steps, and said 104.
  const seen = new Set<string>();
  const edges: { from: string; to: string; label?: string }[] = [];
  for (const id of ids) {
    for (const target of flowTargets(nodes[id])) {
      const key = `${id}\u0000${target}`;
      if (!nodes[target] || seen.has(key)) continue;
      seen.add(key);
      edges.push({ from: id, to: target });
    }
  }

  return { nodes: placed, edges, columns: maxDepth + 1 };
}

// ------------------------------------------------------------------ billing --

export interface Wallet {
  balance: number;
  /** Money committed to messages that have been accepted but not yet sent. */
  held: number;
  /** balance - held. What a new send is actually checked against. */
  available: number;
  isActive: boolean;
  lowBalanceAlert: number;
  suspensionReason: string | null;
}

/** 0 debit, 1 credit, 2 refund, 3 maintenance, 4 adjustment. */
export interface LedgerEntry {
  id: number;
  kind: number;
  amount: number;
  balanceAfter: number;
  externalRef: string | null;
  description: string | null;
  createdAt: string;
  messageId: number | null;
  templateName: string | null;
}

export interface Statement {
  month: string;
  opening: number;
  closing: number;
  totals: { spent: number | null; added: number | null; adjusted: number | null; entries: number };
  byCategory: { category: string; messages: number; amount: number }[];
}

export interface BillingProfile {
  legalName: string;
  gstin: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateCode: string | null;
  postalCode: string | null;
  billingEmail: string | null;
}

export interface Invoice {
  id: number;
  number: string;
  issuedAt: string;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  total: number;
  status: number;
}

export interface TopUpOrder {
  paymentId: number;
  orderId: string;
  keyId: string;
  taxable: number;
  gross: number;
  cgst: number;
  sgst: number;
  igst: number;
  currency: string;
  /** The hosted checkout page for this order — what the mobile app opens. Signed, expires. */
  checkoutUrl: string;
  expiresAt: string;
}

/** What Razorpay Checkout hands the browser when a payment goes through. */
export interface CheckoutResult {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface PaymentState {
  paymentId: number;
  status: 'created' | 'authorized' | 'captured' | 'failed' | 'refunded' | 'unknown';
  credited: boolean;
  taxable: number;
  gross: number;
  failureReason: string | null;
  orderId: string | null;
}

export interface CategoryPrice {
  category: string;
  unitPrice: number;
  defaultPrice?: number;
  isOverride?: boolean;
  updatedAt: string | null;
}

/**
 * GST state codes. Only the ones a client is plausibly registered in — the list is
 * for a picker, not for validation, which the server does by length.
 */
export const GST_STATES: { code: string; name: string }[] = [
  { code: '27', name: 'Maharashtra' },
  { code: '29', name: 'Karnataka' },
  { code: '07', name: 'Delhi' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '36', name: 'Telangana' },
  { code: '24', name: 'Gujarat' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '19', name: 'West Bengal' },
  { code: '08', name: 'Rajasthan' },
  { code: '32', name: 'Kerala' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '06', name: 'Haryana' },
  { code: '03', name: 'Punjab' },
  { code: '30', name: 'Goa' },
  { code: '10', name: 'Bihar' },
  { code: '21', name: 'Odisha' },
  { code: '20', name: 'Jharkhand' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '04', name: 'Chandigarh' },
  { code: '18', name: 'Assam' },
];

export const LEDGER_KINDS: Record<number, string> = {
  0: 'Sent',
  1: 'Top-up',
  2: 'Refund',
  3: 'Correction',
  4: 'Adjustment',
};

/** Indian grouping, two decimals, no currency symbol duplication. */
export function formatMoney(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ---------------------------------------------------------------- analytics --

export interface ReportDay {
  day: string;
  inbound: number;
  outbound: number;
  failed: number;
  spend: number;
  opened: number;
  resolved: number;
  reopened: number;
}

export interface ReportTotals {
  inbound: number;
  outbound: number;
  failed: number;
  spend: number;
  opened: number;
  resolved: number;
  reopened: number;
  /** Minutes. Null when nothing in the range could contribute a figure. */
  firstResponseOpen: number | null;
  firstResponseWall: number | null;
  resolutionOpen: number | null;
  resolutionWall: number | null;
  measuredResponses: number;
  measuredResolutions: number;
  unknownResponses: number;
  unknownResolutions: number;
}

export interface ReportOverview {
  from: string;
  to: string;
  /** The last day of the range has not finished yet. */
  includesToday: boolean;
  days: ReportDay[];
  totals: ReportTotals;
}

export interface AgentReportRow {
  agentId: number;
  displayName: string;
  messagesSent: number;
  resolved: number;
  assigned: number;
  frSum: number;
  frCount: number;
  resSum: number;
  resCount: number;
}

/** One step of a bot, named the way the builder names it, with how many conversations it counts for. */
export interface BotReportStep {
  node: string;
  label: string;
  kind: string;
  count: number;
}

export interface BotReportDay {
  date: string;
  total: number;
  completed: number;
  handedOff: number;
  leftEarly: number;
  problems: number;
}

export interface BotReportRecent {
  conversationId: number;
  displayName: string | null;
  waId: string;
  endedAt: string;
  outcome: string;
  turns: number;
  lastNode: string | null;
  lastLabel: string | null;
}

export interface BotReportTotals {
  total: number;
  completed: number;
  handedOff: number;
  leftEarly: number;
  problems: number;
}

/** The Bots report: one bot, one range, everything a client asks about it. */
export interface BotReport extends BotReportTotals {
  /** The bots there is anything to say about, live ones first. */
  names: string[];
  /** The bot this report is about, or null when the account has none. */
  name: string | null;
  from: string;
  to: string;
  includesToday?: boolean;
  byOutcome?: Record<string, number>;
  /** The same four figures for the period of equal length before this one. */
  previous?: BotReportTotals;
  medianTurns?: number;
  /** Minutes from first message to the end step, median of finished conversations; null when none qualify. */
  medianMinutes?: number | null;
  days?: BotReportDay[];
  /** Conversations ending in each IST hour, 24 buckets from midnight. */
  hours?: number[];
  reached?: BotReportStep[];
  endings?: BotReportStep[];
  dropOff?: BotReportStep[];
  recent?: BotReportRecent[];
}

/** Outcome codes from the engine, in the words a client reads. */
export const BOT_OUTCOME_LABEL: Record<string, string> = {
  completed: 'Finished the flow',
  handed_off: 'Passed to a person',
  abandoned: 'Went quiet',
  gave_up: 'Bot gave up on the answer',
  failed: 'Error',
  no_match: 'No matching bot',
  disabled: 'Bot switched off',
  send_cap: 'Too many messages in one turn',
  undeliverable: 'Could not be delivered',
  jumped: 'Handed to another bot',
};

export interface TemplateReportRow {
  templateName: string;
  sent: number;
  delivered: number;
  readCount: number;
  failed: number;
  clicked: number;
  spend: number;
}

// ------------------------------------------------------------- catalogue --

/** One product from the tenant's Commerce Manager catalogue, as mirrored. */
export interface CatalogProduct {
  retailerId: string;
  productId: string;
  name: string;
  description: string | null;
  /** As Meta formats it: "10,000.00 INR". */
  price: string | null;
  currency: string | null;
  imageUrl: string | null;
  availability: string | null;
  /** approved, pending, rejected, outdated — WhatsApp's verdict on this listing. */
  reviewStatus: string | null;
  /** The product's own web page, when one was given. */
  link: string | null;
  /** Meta's rejection reasons, comma-joined, when reviewStatus is rejected. */
  reviewReasons: string | null;
  /** FETCHED, FETCH_FAILED… — whether Meta managed to pull the picture. */
  imageStatus: string | null;
  /** The pictures after the first, in the order WhatsApp shows them. */
  extraImageUrls: string[];
}

/** What the editor sends. Price in major units as typed ("25500.00"); the server converts. */
export interface ProductInput {
  retailerId?: string | null;
  name?: string | null;
  description?: string | null;
  price?: string | null;
  currency?: string | null;
  imageUrl?: string | null;
  link?: string | null;
  availability?: 'in stock' | 'out of stock' | null;
  originCountry?: string | null;
  /** Pictures after the first. Omit on an edit to leave them alone; [] clears them. */
  extraImageUrls?: string[] | null;
}

export interface CatalogState {
  catalogId: string | null;
  name: string | null;
  productCount: number;
  syncedAt: string | null;
  /** Why the last refresh failed, when it did; the products shown are the previous copy. */
  syncProblem: string | null;
}

/** An item in a basket a customer sent, with the name frozen at arrival. */
export interface OrderItem {
  retailerId: string | null;
  name: string | null;
  imageUrl: string | null;
  quantity: number;
  price: number;
  currency: string | null;
}

export interface OrderPayload {
  catalogId: string | null;
  text: string | null;
  currency: string | null;
  total: number;
  items: OrderItem[];
}

/** The order on an inbound `order` message, or null when the row is not one (or is malformed). */
export function parseOrder(payload: string | null | undefined): OrderPayload | null {
  if (!payload) return null;
  try {
    const raw = JSON.parse(payload) as Partial<OrderPayload>;
    if (!raw || !Array.isArray(raw.items)) return null;
    return {
      catalogId: raw.catalogId ?? null,
      text: raw.text ?? null,
      currency: raw.currency ?? null,
      total: Number(raw.total ?? 0),
      items: raw.items.map((i) => ({
        retailerId: i.retailerId ?? null,
        name: i.name ?? null,
        imageUrl: i.imageUrl ?? null,
        quantity: Number(i.quantity ?? 0),
        price: Number(i.price ?? 0),
        currency: i.currency ?? null,
      })),
    };
  } catch {
    return null;
  }
}

/** What a sent template looked like besides its body: drawn by the bubble as the card the customer saw. */
export interface TemplateCard {
  header: { format: string; text: string | null } | null;
  footer: string | null;
  buttons: { type: string; text: string }[];
}

export function parseTemplateCard(payload: string | null | undefined): TemplateCard | null {
  if (!payload) return null;
  try {
    const raw = JSON.parse(payload) as Partial<TemplateCard>;
    if (!raw || typeof raw !== 'object') return null;
    const buttons = Array.isArray(raw.buttons)
      ? raw.buttons.filter((b) => b && typeof b.text === 'string').map((b) => ({ type: String(b.type ?? 'QUICK_REPLY').toUpperCase(), text: b.text }))
      : [];
    const header = raw.header && typeof raw.header === 'object' && typeof raw.header.format === 'string'
      ? { format: raw.header.format.toUpperCase(), text: raw.header.text ?? null }
      : null;
    if (!header && !raw.footer && buttons.length === 0) return null;
    return { header, footer: raw.footer ?? null, buttons };
  } catch {
    return null;
  }
}

/** "INR 25,500.00" — the way the thread and the order card state money. */
export function formatAmount(amount: number, currency: string | null): string {
  const text = amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${text}` : text;
}

/** A person or desk the Consumer Protection (E-Commerce) Rules require a business in India to name. */
export interface ComplianceContact {
  name: string | null;
  email: string | null;
  landlineNumber: string | null;
  mobileNumber: string | null;
}

/** The record WhatsApp needs before it shows an Indian number's catalogue to anyone in India. */
export interface ComplianceInfo {
  entityName: string | null;
  entityType: string | null;
  entityTypeCustom: string | null;
  isRegistered: boolean | null;
  customerCare: ComplianceContact | null;
  grievanceOfficer: ComplianceContact | null;
}

export const COMPLIANCE_ENTITY_TYPES: { value: string; label: string }[] = [
  { value: 'PRIVATE_COMPANY', label: 'Private limited company' },
  { value: 'PUBLIC_COMPANY', label: 'Public limited company' },
  { value: 'LIMITED_LIABILITY_PARTNERSHIP', label: 'LLP' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'SOLE_PROPRIETORSHIP', label: 'Sole proprietorship' },
  { value: 'OTHER', label: 'Other' },
];

/** What Meta holds for a number: the catalogue switches and the compliance record, read live. */
export interface ChannelCommerce {
  isCatalogVisible: boolean | null;
  isCartEnabled: boolean | null;
  settingsProblem: string | null;
  compliance: ComplianceInfo | null;
  complianceProblem: string | null;
  entityTypes: string[];
  /** Whether the stored token can read the catalogue — what the picker and the product editor need. */
  catalogue: { id: string | null; name: string | null; problem: string | null };
}

export interface SlaPolicy {
  firstResponseMinutes: number | null;
  resolutionMinutes: number | null;
  /** 0 business hours, 1 wall clock. */
  primaryClock: number;
  isActive: boolean;
}

/** The policy plus what it is doing right now — the setup screen's status strip. */
export interface SlaStatus extends SlaPolicy {
  /** Working hours are switched on and have at least one window; otherwise a
      working-hours target runs on the clock, and the screen says so. */
  hoursEnabled: boolean;
  /** Open conversations over a target at this moment. */
  breached: number;
  /** Open conversations carrying a deadline — the ones the rule is watching. */
  measured: number;
}

/**
 * "Due in 12m" / "Overdue 1h 5m" for a running deadline. Null when there is none
 * to show. Whole minutes: a countdown in seconds in a list of forty rows is forty
 * things ticking.
 */
export function describeDue(dueAt: string | null | undefined, now: number = Date.now()): { text: string; overdue: boolean } | null {
  if (!dueAt) return null;
  const diff = Math.round((new Date(dueAt).getTime() - now) / 60_000);
  if (Number.isNaN(diff)) return null;
  if (diff < 0) return { text: `Overdue ${formatMinutes(-diff)}`, overdue: true };
  return { text: diff === 0 ? 'Due now' : `Due in ${formatMinutes(diff)}`, overdue: false };
}

/**
 * Minutes as something a person reads. Deliberately coarse above an hour: "2h 14m"
 * is what somebody wants from a response time, and "134 minutes" is arithmetic they
 * have to do themselves.
 */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);

  if (hours < 24) return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;

  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

// ------------------------------------------------------------ platform admin --

/** One row of the audit trail. */
export interface AuditRow {
  id: number;
  tenantId: number | null;
  tenantName: string | null;
  /** 0 platform user, 1 agent, 2 API key, 3 the platform itself. */
  actorType: number;
  actorId: number | null;
  /** The actor's name as it was at the time, not as it is now. */
  actorLabel: string | null;
  action: string;
  resourceType: string | null;
  resourceId: number | null;
  /** JSON: what changed, not the whole row. */
  detail: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AuditPage {
  rows: AuditRow[];
  nextBeforeId: number | null;
}

export interface PlatformDay {
  day: string;
  inbound: number;
  outbound: number;
  failed: number;
  spend: number;
}

export interface PlatformTotals {
  inbound: number;
  outbound: number;
  failed: number;
  /** The headline. Exclusive of GST, because the tax is not ours. */
  cashReceived: number;
  invoiced: number;
  creditConsumed: number;
  paymentCount: number;
  activeTenants: number;
  suspendedTenants: number;
  /** Paid for and not yet used. A liability, not revenue. */
  outstandingCredit: number;
}

export interface PlatformOverview {
  from: string;
  to: string;
  days: PlatformDay[];
  totals: PlatformTotals;
}

export interface TenantMoneyRow {
  tenantId: number;
  companyName: string;
  status: number;
  balance: number;
  held: number;
  messagesSent: number;
  creditConsumed: number;
  cashReceived: number;
}

export interface PlatformUser {
  id: number;
  email: string;
  displayName: string;
  isSuperuser: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** 0 suspended, 1 active, 2 closed. Closed is not deleted: a tenant owns history. */
export const TENANT_STATUS: Record<number, string> = {
  0: 'Suspended',
  1: 'Active',
  2: 'Closed',
};

export const AUDIT_ACTOR: Record<number, string> = {
  0: 'Platform',
  1: 'Agent',
  2: 'API key',
  3: 'System',
};

/**
 * The four groups the audit screen filters by, as action prefixes.
 *
 * Mirrors AuditActions.Groups on the server. It is duplicated rather than fetched
 * because it is a label on a dropdown, and a screen that cannot draw its own filter
 * until a request comes back is a screen that flickers.
 */
export const AUDIT_GROUPS: { key: string; label: string; prefixes: string[] }[] = [
  { key: 'access', label: 'Permissions and access', prefixes: ['role.', 'agent.', 'password.', 'api_key.', 'login.'] },
  { key: 'money', label: 'Money and pricing', prefixes: ['pricing.', 'wallet.', 'invoice.', 'tenant.suspended', 'tenant.reactivated'] },
  { key: 'provisioning', label: 'Provisioning', prefixes: ['tenant.created', 'tenant.updated', 'tenant.closed', 'meta_app.', 'channel.'] },
  { key: 'bulk', label: 'Bulk customer actions', prefixes: ['campaign.', 'contacts.', 'retention.', 'segment.'] },
];

// ------------------------------------------------------------- developer API --

export interface ApiKeySummary {
  id: number;
  name: string;
  keyPrefix: string;
  /** Space-separated permissions, or null meaning every permission — a key issued before scopes existed. */
  scopes: string | null;
  ratePerMinute: number;
  lastUsedAt: string | null;
  createdAt: string;
}

export interface WebhookSubscription {
  id: number;
  url: string;
  /** Space-separated event names. Empty means every event. */
  events: string;
  isActive: boolean;
  consecutiveFailures: number;
  pausedAt: string | null;
  pauseReason: string | null;
  failedCount: number;
  createdAt: string;
}

export interface WebhookDelivery {
  id: number;
  eventId: string;
  event: string;
  /** 0 waiting, 1 delivered, 2 gave up. */
  status: number;
  attempts: number;
  lastStatusCode: number | null;
  lastError: string | null;
  nextAttemptAt: string;
  createdAt: string;
  deliveredAt: string | null;
}

// ------------------------------------------------------------------- inbox --

/**
 * One system pill in a thread.
 *
 * `detail` is JSON and records values **as they were set**, never a reference —
 * a custom field renamed next year must not rewrite what a pill said last Tuesday.
 */
export interface ConversationEvent {
  id: number;
  kind: string;
  detail: string | null;
  /** 0 platform user, 1 agent, 2 API key, 3 the platform itself. */
  actorType: number;
  actorId: number | null;
  actorLabel: string | null;
  createdAt: string;
}

export interface Collaborator {
  agentId: number;
  displayName: string;
  email: string;
  isActive: boolean;
  addedAt: string;
  addedByName: string | null;
}

export interface BotRun {
  id: number;
  flowId: number | null;
  flowName: string | null;
  flowVersion: number | null;
  currentNode: string | null;
  status: number;
  variables: string | null;
  startedAt: string;
  updatedAt: string;
}

/**
 * A system pill, as a sentence.
 *
 * The server stores a kind and a JSON detail; the wording lives here because it is
 * wording. A kind the client does not know renders as nothing at all — a silent gap
 * in the timeline — so the fallback is the kind itself rather than an empty string.
 */
export function describeEvent(event: ConversationEvent): string {
  let detail: Record<string, string> = {};
  try {
    detail = event.detail ? (JSON.parse(event.detail) as Record<string, string>) : {};
  } catch {
    detail = {};
  }

  const by = event.actorLabel ? ` by ${event.actorLabel}` : '';

  switch (event.kind) {
    case 'field.set':
      return `Custom field "${detail.field}" set to "${detail.value ?? ''}"${by}`;
    case 'assigned':
      return `Assigned to ${detail.agent ?? 'somebody'}${by}`;
    case 'unassigned':
      return `Unassigned${by}`;
    case 'resolved':
      return `Marked resolved${by}`;
    case 'reopened':
      return 'Reopened by a new message';
    case 'collaborator.added':
      return `${detail.agent ?? 'Somebody'} added as a collaborator${by}`;
    case 'collaborator.removed':
      return `${detail.agent ?? 'Somebody'} removed as a collaborator${by}`;
    case 'tag.added':
      return `Tagged "${detail.tag}"${by}`;
    case 'tag.removed':
      return `Tag "${detail.tag}" removed${by}`;
    case 'opted_out':
      return `Opted out of messages${by}`;
    case 'bot.handoff':
      return 'Handed over from the bot';
    // Blocked is ours, opted_out is theirs — the two sentences say which, because
    // a timeline that renders both as "stopped messaging" loses the only fact a
    // reader of it needs.
    // `whatsapp` is what Meta said when asked to block them too: "Blocked at
    // WhatsApp on +91 …" or why not. Without it a reader assumes the block
    // stopped their messages, and a block on our side alone stops nothing inbound.
    case 'blocked':
      return `Blocked${by}${detail.reason ? ` — ${detail.reason}` : ''}${detail.whatsapp ? `. ${detail.whatsapp}` : ''}`;
    case 'unblocked':
      return `Unblocked${by}${detail.whatsapp ? `. ${detail.whatsapp}` : ''}`;
    case 'chat.cleared':
      return `Earlier messages hidden from this pane${by}`;
    default:
      return event.kind;
  }
}

/* ------------------------------------------------------------ notifications -- */

/**
 * The kinds of thing an agent can be told about.
 *
 * These strings are the wire contract with the API — `NotificationKind` in
 * `Wa.Modules/Realtime/IAgentNotifier.cs` holds the same set, and
 * `NotificationKindTests` asserts the two agree. A kind the server sends and this
 * has never heard of is a notification that arrives and renders nothing, which
 * looks exactly like one that was never sent.
 */
export const NOTIFICATION_KIND = {
  newMessage: 'message',
  assigned: 'assigned',
  mention: 'mention',
  slaApproaching: 'sla-approaching',
  slaBreached: 'sla-breached',
  campaignFinished: 'campaign-finished',
  templateStatus: 'template-status',
  /** The plan is ending or has ended. Goes to settings.write agents; no switch. */
  subscription: 'subscription',
  /** Fired by the Notifications screen's own button. Never filtered. */
  test: 'test',
} as const;

/**
 * What one agent wants to be told, and how.
 *
 * Two groups, and the split matters: <b>the events are enforced by the server</b>
 * — an agent with `mention` off is not in the audience and the event never reaches
 * their browser — while <b>the surfaces are applied here</b>, because how a thing
 * is presented is not the server's business and two agents in one audience can
 * want it presented differently. `onlyMyChats` is a server-side narrowing too.
 */
export interface NotificationPreferences {
  desktop: boolean;
  sound: boolean;
  badge: boolean;

  newMessage: boolean;
  assigned: boolean;
  mention: boolean;
  slaApproaching: boolean;
  slaBreached: boolean;
  campaignFinished: boolean;
  templateStatus: boolean;

  onlyMyChats: boolean;
}

/**
 * What an agent who has never opened the screen has — the same values as the
 * server's `NotificationPreferences.Defaults`, and used only while the real ones
 * are still loading, so a notification arriving in the first second of a page load
 * is not silently dropped.
 */
export const NOTIFICATION_DEFAULTS: NotificationPreferences = {
  desktop: true,
  sound: true,
  badge: true,
  newMessage: true,
  assigned: true,
  mention: true,
  slaApproaching: true,
  slaBreached: true,
  campaignFinished: true,
  templateStatus: true,
  onlyMyChats: false,
};

// ------------------------------------------------------------------ shop ----

/** The merchant's shop settings. Secrets never come back; only whether they are set. */
export interface ShopSettings {
  tenantId: number;
  slug: string;
  isEnabled: boolean;
  name: string;
  description: string | null;
  logoUrl: string | null;
  bannerUrl: string | null;
  brandColor: string;
  themePreset: string;
  currency: string;
  pricesIncludeTax: boolean;
  hidePricesUntilSignin: boolean;
  orderingPaused: boolean;
  pauseMessage: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  address: string | null;
  minOrderDefault: number;
  deliveryRules: string | null;
  pickupEnabled: boolean;
  pickupNote: string | null;
  codEnabled: boolean;
  onAccountEnabled: boolean;
  onlineEnabled: boolean;
  gateway: string | null;
  gatewayKeyId: string | null;
  gatewaySecretSet: boolean;
  gatewayWebhookSecretSet: boolean;
  gatewayVerifiedAt: string | null;
  catalogueVersion: number;
  createdAt: string;
  updatedAt: string;
}

/** PUT body: every field optional; a secret of "" clears it, undefined keeps it. */
export type ShopSettingsInput = Partial<Omit<ShopSettings,
  'tenantId' | 'gatewaySecretSet' | 'gatewayWebhookSecretSet' | 'gatewayVerifiedAt' | 'catalogueVersion' | 'createdAt' | 'updatedAt'>> & {
  gatewayKeySecret?: string;
  gatewayWebhookSecret?: string;
};

export interface CustomerGroup {
  id: number;
  tenantId: number;
  name: string;
  isDefault: boolean;
  minOrderValue: number | null;
  /** Comma-separated subset of online,cod,on_account. */
  allowedPayMethods: string;
  b2bInvoice: boolean;
  sortOrder: number;
  contacts: number;
}

export interface CustomerGroupInput {
  name?: string;
  minOrderValue?: number | null;
  allowedPayMethods?: string;
  b2bInvoice?: boolean;
  sortOrder?: number;
}

export interface ProductCategory { id: number; name: string; slug: string; sortOrder: number; products: number }

export interface ProductVariant {
  id: number;
  productId: number;
  name: string;
  sku: string | null;
  /** JSON object text, e.g. {"finish":"Chrome","size":"600 mm"}. */
  attributes: string | null;
  listPrice: number | null;
  stock: number | null;
  isActive: boolean;
  sortOrder: number;
}

export interface ProductImage { id: number; url: string; sortOrder: number; width: number | null; height: number | null }

export interface Product {
  id: number;
  tenantId: number;
  slug: string;
  name: string;
  description: string | null;
  categoryId: number | null;
  categoryName: string | null;
  sku: string | null;
  hsn: string | null;
  taxRate: number;
  listPrice: number;
  unit: string | null;
  isActive: boolean;
  /** JSON array of group ids, or null for everyone. */
  visibleGroups: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductDetail { product: Product; variants: ProductVariant[]; images: ProductImage[] }

export interface ProductListItem {
  id: number;
  slug: string;
  name: string;
  categoryId: number | null;
  categoryName: string | null;
  sku: string | null;
  listPrice: number;
  isActive: boolean;
  variants: number;
  firstImage: string | null;
  stock: number | null;
  updatedAt: string;
}

export interface VariantInput {
  id?: number | null;
  name?: string;
  sku?: string | null;
  attributes?: string | null;
  listPrice?: number | null;
  stock?: number | null;
  isActive?: boolean;
  sortOrder?: number;
}

export interface ShopProductInput {
  name?: string;
  slug?: string | null;
  description?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  sku?: string | null;
  hsn?: string | null;
  taxRate?: number;
  listPrice?: number;
  unit?: string | null;
  isActive?: boolean;
  visibleGroups?: number[] | null;
  sortOrder?: number;
  variants?: VariantInput[];
  images?: string[];
}

export interface PriceRow { variantId: number; groupId: number | null; contactId: number | null; minQty: number; price: number }
export interface PriceRowInput { variantId: number; groupId?: number | null; minQty?: number; price: number }

export interface ShopImportRowOutcome { row: number; action: 'create' | 'update' | 'error'; product: string | null; variant: string | null; error: string | null }
export interface ShopImportPreview {
  rows: number; creates: number; updates: number; errors: number; groups: string[]; outcomes: ShopImportRowOutcome[]; fileError: string | null;
}
